// @flow

import { createActions, handleActions } from 'redux-actions'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import RestClient from 'frontend_core/dist/rest/client'
import { SET_UI_STATE } from './ui'
import { actions as RoomActions } from './room'
import { actions as LocalRoomsActions } from './local/rooms'
import { type FavoriteList } from 'types/favorite'

const PER_PAGE_FOR_FAVORITE_LIST_DETAIL = 20

/**
 ************************************************************
 * 初期ステート
 ************************************************************
 **/

export type State = {
  favoriteList: Array<FavoriteList>,
  favoriteListDetail: FavoriteList,
  registeredFavoriteLists: ?Array<FavoriteList>,
  isEmpty: boolean,
  fetchFavoriteListDetailPage: number,
  isSuccessFetchFavoriteList: boolean,
  failedFetchFavoriteListMessage: ?string,
  isSuccessFetchFavoriteListDetail: boolean,
  isFetchFavoriteListDetailEnd: boolean,
  failedFetchFavoriteListDetailMessage: ?string,
  failedCreateFavoriteListMessage: ?string,
  isFailedFavoriteMemo: boolean,
  isSuccessFavoriteMemo: boolean,
  failedUpdateFavoriteListMessage: ?string,
  isSuccessUpdateFavoriteList: boolean,
  failedDeleteFavoriteListMessage: ?string,
  isSuccessDeleteFavoriteList: boolean,
}
const initialState: State = {
  favoriteList: [],
  favoriteListDetail: {
    favorites: [],
  },
  registeredFavoriteLists: null,
  isEmpty: false,
  fetchFavoriteListDetailPage: 1,
  isSuccessFetchFavoriteList: false,
  failedFetchFavoriteListMessage: null,
  isSuccessFetchFavoriteListDetail: false,
  failedFetchFavoriteListDetailMessage: null,
  failedCreateFavoriteListMessage: null,
  isFailedFavoriteMemo: false,
  isSuccessFavoriteMemo: false,
  failedUpdateFavoriteListMessage: null,
  isSuccessUpdateFavoriteList: false,
  failedDeleteFavoriteListMessage: null,
  isSuccessDeleteFavoriteList: false,
  isFetchFavoriteListDetailEnd: false,
}

/**
 ************************************************************
 * ActionTypes
 ************************************************************
 **/

const actionTypes = {
  FETCH_FAVORITE_LIST: 'FETCH_FAVORITE_LIST',
  SET_FAVORITE_LIST: 'SET_FAVORITE_LIST',
  FAILED_FAVORITE_LIST: 'FAILED_FAVORITE_LIST',

  INITIALIZE_FAVORITE_LIST_DETAIL: 'INITIALIZE_FAVORITE_LIST_DETAIL',
  FETCH_FAVORITE_LIST_DETAIL: 'FETCH_FAVORITE_LIST_DETAIL',
  SET_FAVORITE_LIST_DETAIL: 'SET_FAVORITE_LIST_DETAIL',
  FAILED_FAVORITE_LIST_DETAIL: 'FAILED_FAVORITE_LIST_DETAIL',

  SET_REGISTERED_FAVORITE_LISTS: 'SET_REGISTERED_FAVORITE_LISTS',

  CREATE_FAVORITE_LIST: 'CREATE_FAVORITE_LIST',
  ADD_FAVORITE_LIST: 'ADD_FAVORITE_LIST',
  FAILED_CREATE_FAVORITE_LIST: 'FAILED_CREATE_FAVORITE_LIST',

  ADD_FAVORITE: 'ADD_FAVORITE',
  SUCCESS_ADD_FAVORITE: 'SUCCESS_ADD_FAVORITE',
  REMOVE_FAVORITE: 'REMOVE_FAVORITE',
  SUCCESS_REMOVE_FAVORITE: 'SUCCESS_REMOVE_FAVORITE',
  FAILED_CREATE_FAVORITE: 'FAILED_CREATE_FAVORITE',
  FAILED_DELETE_FAVORITE: 'FAILED_DELETE_FAVORITE',

  UPDATE_FAVORITE_MEMO: 'UPDATE_FAVORITE_MEMO',
  SUCCESS_FAVORITE_MEMO: 'SUCCESS_FAVORITE_MEMO',
  FAILED_FAVORITE_MEMO: 'FAILED_FAVORITE_MEMO',

  UPDATE_FAVORITE_LIST: 'UPDATE_FAVORITE_LIST',
  SUCCESS_UPDATE_FAVORITE_LIST: 'SUCCESS_UPDATE_FAVORITE_LIST',
  FAILED_UPDATE_FAVORITE_LIST: 'FAILED_UPDATE_FAVORITE_LIST',

  DELETE_FAVORITE_LIST: 'DELETE_FAVORITE_LIST',
  SUCCESS_DELETE_FAVORITE_LIST: 'SUCCESS_DELETE_FAVORITE_LIST',
  FAILED_DELETE_FAVORITE_LIST: 'FAILED_DELETE_FAVORITE_LIST',

  ADJUST_FAVORITE_LIST_DETAIL: 'ADJUST_FAVORITE_LIST_DETAIL',
}

/**
 ************************************************************
 * Actions
 ************************************************************
 **/

export const actions = createActions(
  actionTypes.FETCH_FAVORITE_LIST,
  actionTypes.SET_FAVORITE_LIST,
  actionTypes.FAILED_FAVORITE_LIST,
  actionTypes.INITIALIZE_FAVORITE_LIST_DETAIL,
  actionTypes.FETCH_FAVORITE_LIST_DETAIL,
  actionTypes.SET_FAVORITE_LIST_DETAIL,
  actionTypes.FAILED_FAVORITE_LIST_DETAIL,
  actionTypes.SET_REGISTERED_FAVORITE_LISTS,
  actionTypes.CREATE_FAVORITE_LIST,
  actionTypes.ADD_FAVORITE_LIST,
  actionTypes.FAILED_CREATE_FAVORITE_LIST,
  actionTypes.ADD_FAVORITE,
  actionTypes.SUCCESS_ADD_FAVORITE,
  actionTypes.REMOVE_FAVORITE,
  actionTypes.SUCCESS_REMOVE_FAVORITE,
  actionTypes.FAILED_CREATE_FAVORITE,
  actionTypes.SUCCESS_FAVORITE_MEMO,
  actionTypes.UPDATE_FAVORITE_MEMO,
  actionTypes.FAILED_FAVORITE_MEMO,
  actionTypes.UPDATE_FAVORITE_LIST,
  actionTypes.SUCCESS_UPDATE_FAVORITE_LIST,
  actionTypes.FAILED_UPDATE_FAVORITE_LIST,
  actionTypes.DELETE_FAVORITE_LIST,
  actionTypes.SUCCESS_DELETE_FAVORITE_LIST,
  actionTypes.FAILED_DELETE_FAVORITE_LIST,
  actionTypes.ADJUST_FAVORITE_LIST_DETAIL,
  actionTypes.FAILED_FAVORITE_LIST,
  actionTypes.FAILED_DELETE_FAVORITE_LIST,
)

/**
 ************************************************************
 * reducer
 ************************************************************
 **/

export const reducer = handleActions(
  {
    [actionTypes.FETCH_FAVORITE_LIST]: (state, action) => ({
      ...state,
      favoriteList: action.payload.silent ? state.favoriteList : [],
      isSuccessFetchFavoriteList: action.payload.silent
        ? state.isSuccessFetchFavoriteList
        : false,
    }),
    [actionTypes.SET_FAVORITE_LIST]: (state, action) => ({
      ...state,
      favoriteList: action.payload,
      isSuccessFetchFavoriteList: true,
      isEmpty: action.payload.length === 0,
    }),
    [actionTypes.FAILED_FAVORITE_LIST]: (state, action) => ({
      ...state,
      failedFetchFavoriteListMessage: action.payload,
    }),
    [actionTypes.INITIALIZE_FAVORITE_LIST_DETAIL]: (state, action) => ({
      ...state,
      failedFetchFavoriteListDetailMessage: null,
      fetchFavoriteListDetailPage: 1,
      favoriteListDetail: action.payload.initialFavoriteList,
      isSuccessFetchFavoriteListDetail: false,
      isFetchFavoriteListDetailEnd: false,
    }),
    [actionTypes.FETCH_FAVORITE_LIST_DETAIL]: state => ({
      ...state,
      failedFetchFavoriteListDetailMessage: null,
      isSuccessFetchFavoriteListDetail: false,
    }),
    [actionTypes.SET_FAVORITE_LIST_DETAIL]: (state, action) => ({
      ...state,
      favoriteListDetail: Object.assign({}, state.favoriteListDetail, {
        ...action.payload,
        favorites: (state.favoriteListDetail.favorites || []).concat(
          action.payload.favorites,
        ),
      }),
      isSuccessFetchFavoriteListDetail: true,
      isFetchFavoriteListDetailEnd:
        action.payload.favorites.length < PER_PAGE_FOR_FAVORITE_LIST_DETAIL,
      fetchFavoriteListDetailPage: state.fetchFavoriteListDetailPage + 1,
    }),
    [actionTypes.FAILED_FAVORITE_LIST_DETAIL]: (state, action) => ({
      ...state,
      failedFetchFavoriteListDetailMessage: action.payload,
    }),
    [actionTypes.SET_REGISTERED_FAVORITE_LISTS]: (state, action) => ({
      ...state,
      registeredFavoriteLists: action.payload,
    }),
    [actionTypes.CREATE_FAVORITE_LIST]: state => ({
      ...state,
    }),
    [actionTypes.ADD_FAVORITE_LIST]: (state, action) => ({
      ...state,
      favoriteList: [].concat(action.payload, state.favoriteList),
    }),
    [actionTypes.FAILED_CREATE_FAVORITE_LIST]: (state, action) => ({
      ...state,
      failedCreateFavoriteListMessage: action.payload,
    }),
    [actionTypes.ADD_FAVORITE]: state => ({
      ...state,
      failedCreateFavoriteMessage: null,
    }),
    [actionTypes.SUCCESS_ADD_FAVORITE]: (state, action) => ({
      ...state,
      favoriteList: state.favoriteList.map(
        list =>
          list.id === action.payload.favoriteListId
            ? { ...list, total_favorites: list.total_favorites + 1 }
            : list,
      ),
      favoriteListDetail: {
        ...state.favoriteListDetail,
        total_favorites:
          state.favoriteListDetail.id === action.payload.favoriteListId
            ? state.favoriteListDetail.total_favorites + 1
            : state.favoriteListDetail.total_favorites,
        favorites: (state.favoriteListDetail.favorites || []).map(
          item =>
            item.id === action.payload.favorableId
              ? {
                  ...item,
                  registered_favorite_lists: [
                    ...item.registered_favorite_lists,
                    action.payload.favoriteListId,
                  ],
                }
              : item,
        ),
      },
    }),
    [actionTypes.REMOVE_FAVORITE]: state => ({
      ...state,
      failedDeleteFavoriteMessage: null,
    }),
    [actionTypes.SUCCESS_REMOVE_FAVORITE]: (state, action) => ({
      ...state,
      favoriteList: state.favoriteList.map(
        list =>
          list.id === action.payload.favoriteListId
            ? { ...list, total_favorites: list.total_favorites - 1 }
            : list,
      ),
      favoriteListDetail: {
        ...state.favoriteListDetail,
        total_favorites:
          state.favoriteListDetail.id === action.payload.favoriteListId
            ? state.favoriteListDetail.total_favorites - 1
            : state.favoriteListDetail.total_favorites,
        favorites: (state.favoriteListDetail.favorites || []).map(
          item =>
            item.id === action.payload.favorableId
              ? {
                  ...item,
                  registered_favorite_lists: item.registered_favorite_lists.filter(
                    innerItem => innerItem !== action.payload.favoriteListId,
                  ),
                }
              : item,
        ),
      },
    }),
    [actionTypes.FAILED_CREATE_FAVORITE]: (state, action) => ({
      ...state,
      failedCreateFavoriteMessage: action.payload,
    }),
    [actionTypes.FAILED_DELETE_FAVORITE]: (state, action) => ({
      ...state,
      failedDeleteFavoriteMessage: action.payload,
    }),
    [actionTypes.UPDATE_FAVORITE_MEMO]: state => ({
      ...state,
      isFailedFavoriteMemo: false,
      isSuccessFavoriteMemo: false,
      failedFavoriteMemoMessage: null,
    }),
    [actionTypes.SUCCESS_FAVORITE_MEMO]: (state: State, action) => ({
      ...state,
      favoriteListDetail: {
        ...state.favoriteListDetail,
        favorites: (state.favoriteListDetail.favorites || []).map(
          (favorite, index) => ({
            ...favorite,
            memo:
              index === action.payload.favoriteIndex
                ? action.payload.favorite.memo
                : favorite.memo,
          }),
        ),
      },
      isSuccessFavoriteMemo: true,
    }),
    [actionTypes.FAILED_FAVORITE_MEMO]: (state: State, action) => ({
      ...state,
      isFailedFavoriteMemo: true,
      failedFavoriteMemoMessage: action.payload,
    }),
    [actionTypes.UPDATE_FAVORITE_LIST]: state => ({
      ...state,
      isSuccessUpdateFavoriteList: false,
    }),
    [actionTypes.SUCCESS_UPDATE_FAVORITE_LIST]: (state: State, action) => ({
      ...state,
      favoriteList: state.favoriteList.map(
        favoriteList =>
          favoriteList.id === action.payload.id ? action.payload : favoriteList,
      ),
      favoriteListDetail: action.payload,
      isSuccessUpdateFavoriteList: true,
    }),
    [actionTypes.FAILED_UPDATE_FAVORITE_LIST]: (state, action) => ({
      ...state,
      failedUpdateFavoriteListMessage: action.payload,
    }),
    [actionTypes.DELETE_FAVORITE_LIST]: state => ({
      ...state,
      isSuccessDeleteFavoriteList: false,
    }),
    [actionTypes.SUCCESS_DELETE_FAVORITE_LIST]: (state: State, action) => ({
      ...state,
      favoriteList: state.favoriteList.filter(
        favoriteList => favoriteList.id !== action.payload,
      ),
      favoriteListDetail: {},
      isSuccessDeleteFavoriteList: true,
    }),
    [actionTypes.FAILED_DELETE_FAVORITE_LIST]: (state, action) => ({
      ...state,
      failedDeleteFavoriteListMessage: action.payload,
    }),
    [actionTypes.ADJUST_FAVORITE_LIST_DETAIL]: (state, action) => ({
      ...state,
      favoriteListDetail: {
        ...state.favoriteListDetail,
        favorites: (state.favoriteListDetail.favorites || []).filter(
          item =>
            !(
              item.id === action.payload.favorableId &&
              !item.registered_favorite_lists.includes(
                action.payload.favoriteListId,
              )
            ),
        ),
      },
    }),
  },
  initialState,
)

/**
 ************************************************************
 * API連携 非同期アクション
 ************************************************************
 **/

// お気に入りに追加
function* addFavorite({
  payload,
}: {
  payload: {
    userId: string,
    roomId: string,
    favoriteListId?: string,
    showingFavoriteListTooltipList?: Array,
  },
}) {
  const {
    userId,
    roomId,
    favoriteListId,
    showingFavoriteListTooltipList,
  } = payload
  try {
    let path = ''
    if (favoriteListId) {
      path = `/1/users/${userId}/favorite_lists/${favoriteListId}/favorites`
    } else {
      path = `1/users/${userId}/favorites`
    }

    const addFavoriteResult = yield call(RestClient.postJson, path, {
      favorable_type: 'Room',
      favorable_id: roomId,
    })

    const addedFavoriteListName =
      (addFavoriteResult.favorite_list || {}).name || ''

    const state = yield select()

    if (favoriteListId) {
      yield put({
        type: actionTypes.SET_REGISTERED_FAVORITE_LISTS,
        payload: [].concat(
          { id: `${favoriteListId || ''}` },
          state.favorite.registeredFavoriteLists || [],
        ),
      })
    }

    if (state.room.roomDetail.id === roomId) {
      yield put(RoomActions.updateRoomDetail({ isFavorite: true, roomId }))
    }
    yield put(
      LocalRoomsActions.updateRoomById({
        id: roomId,
        room: { isFavorite: true },
      }),
    )

    // お気に入りリスト指定無しの場合、tooltip表示用のフラグを4秒立てる
    if (!favoriteListId && state.room.roomDetail.id === roomId) {
      yield put({
        type: SET_UI_STATE,
        payload: {
          addedFavoriteListName,
          isOpenFavoriteListTooltip: true,
        },
      })
      yield new Promise(res => setTimeout(res, 4000))
      const state = yield select()
      yield put({
        type: SET_UI_STATE,
        payload: {
          addedFavoriteListName:
            state.ui.showingFavoriteListTooltipList &&
            state.ui.showingFavoriteListTooltipList.length > 0
              ? addedFavoriteListName
              : null,
          isOpenFavoriteListTooltip: false,
        },
      })
    } else {
      yield put({
        type: SET_UI_STATE,
        payload: {
          addedFavoriteListName,
          showingFavoriteListTooltipList: (showingFavoriteListTooltipList &&
            showingFavoriteListTooltipList.concat([roomId])) || [roomId],
        },
      })
      yield new Promise(res => setTimeout(res, 4000))
      const state = yield select()
      yield put({
        type: SET_UI_STATE,
        payload: {
          addedFavoriteListName:
            state.ui.showingFavoriteListTooltipList.length > 0
              ? addedFavoriteListName
              : null,
          showingFavoriteListTooltipList:
            (state.ui.showingFavoriteListTooltipList &&
              state.ui.showingFavoriteListTooltipList.filter(
                viewingFavoriteItem => viewingFavoriteItem !== roomId,
              )) ||
            [],
        },
      })
    }
    yield put(
      actions.successAddFavorite({
        favorableId: roomId,
        favoriteListId,
      }),
    )
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_CREATE_FAVORITE,
      payload: e.message,
    })
  }
}

// お気に入りから削除
function* removeFavorite({
  payload,
}: {
  payload: {
    userId: string,
    roomId: string,
    favoriteListId: string,
  },
}) {
  const { userId, roomId, favoriteListId } = payload
  try {
    const path = `/1/users/${userId}/favorite_lists/${favoriteListId}/favorites`

    yield call(RestClient.delete, path, {
      favorable_type: 'Room',
      favorable_id: roomId,
    })

    const state = yield select()
    const registeredFavoriteLists = Object.assign(
      [],
      state.favorite.registeredFavoriteLists,
    )
    ;(state.favorite.registeredFavoriteLists || []).some((list, i) => {
      if (list.id === `${favoriteListId}`) {
        registeredFavoriteLists.splice(i, 1)
      }
    })
    yield put({
      type: actionTypes.SET_REGISTERED_FAVORITE_LISTS,
      payload: Object.assign([], registeredFavoriteLists),
    })

    // 全てのお気に入りを解除したらスペースのお気に入りを解除
    if (registeredFavoriteLists.length === 0) {
      if (state.room.roomDetail.id === roomId) {
        yield put(
          RoomActions.updateRoomDetail({ isFavorite: false, id: roomId }),
        )
      }
      yield put(
        LocalRoomsActions.updateRoomById({
          id: roomId,
          room: { isFavorite: false },
        }),
      )
    } else if (state.room.roomDetail.id === roomId) {
      yield put(RoomActions.updateRoomDetail({ isFavorite: false, id: roomId }))
    }

    yield put(
      actions.successRemoveFavorite({
        favorableId: roomId,
        favoriteListId,
      }),
    )
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_DELETE_FAVORITE,
      payload: e.message,
    })
  }
}

// お気に入りリスト取得
function* fetchFavoriteList({ payload }: { payload: { userId: string } }) {
  const { userId } = payload
  try {
    const favoriteList = yield call(
      RestClient.get,
      `1/users/${userId}/favorite_lists`,
    )
    yield put(actions.setFavoriteList(favoriteList))
  } catch (e) {
    yield put(actions.failedFavoriteList(e.message))
  }
}

function* initializeFavoriteListDetail({
  payload,
}: {
  payload: { userId: string, favoriteListId: number | string },
}) {
  yield put(
    actions.fetchFavoriteListDetail({
      ...payload,
      page: 1,
    }),
  )
}

// お気に入りリスト詳細取得
function* fetchFavoriteListDetail({
  payload,
}: {
  payload: { userId: string, favoriteListId: number | string },
}) {
  const { userId, favoriteListId, page } = payload
  try {
    const favoriteListDetail = yield call(
      RestClient.get,
      `1/users/${userId}/favorite_lists/${favoriteListId}?per_page=${PER_PAGE_FOR_FAVORITE_LIST_DETAIL}&page=${page}`,
    )
    yield put(actions.setFavoriteListDetail(favoriteListDetail))
    yield put({
      type: SET_UI_STATE,
      payload: {
        changeFavoriteListName: favoriteListDetail.name,
      },
    })
  } catch (e) {
    yield put(actions.failedFavoriteListDetail(e.message))
  }
}

// お気に入りリスト作成
function* createFavoriteList({
  payload,
}: {
  payload: { userId: string, roomId?: ?string, name: string },
}) {
  const { userId, roomId, name } = payload
  try {
    const createdFavoriteList = yield call(
      RestClient.postJson,
      `1/users/${userId}/favorite_lists`,
      {
        name,
      },
    )
    yield put(actions.addFavoriteList(createdFavoriteList))

    if (roomId) {
      yield put(
        actions.addFavorite({
          userId,
          roomId,
          favoriteListId: createdFavoriteList.id,
        }),
      )
    }

    yield put({
      type: SET_UI_STATE,
      payload: {
        openInputFavoriteName: false,
      },
    })
  } catch (e) {
    yield put(actions.failedCreateFavoriteList(e.message))
  }
}

function* updateFavoriteList({
  payload,
}: {
  payload: {
    userId: string,
    favoriteListId: number | string,
    name: string,
  },
}) {
  const { userId, favoriteListId, name } = payload
  const path = `1/users/${userId}/favorite_lists/${favoriteListId}`

  try {
    const updatedFavoriteList = yield call(RestClient.patchJson, path, {
      name,
    })

    yield put(actions.successUpdateFavoriteList(updatedFavoriteList))
  } catch (e) {
    yield put(actions.failedUpdateFavoriteList(e.message))
  }
}

// お気に入りリストの削除
function* deleteFavoriteList({
  payload,
}: {
  payload: {
    userId: string,
    favoriteListId: number,
    name: string,
  },
}) {
  const { userId, favoriteListId, name } = payload
  const path = `1/users/${userId}/favorite_lists/${favoriteListId}`

  try {
    yield call(RestClient.delete, path)

    yield put(actions.successDeleteFavoriteList(favoriteListId))
  } catch (e) {
    yield put(actions.failedDeleteFavoriteList(e.message))
  }
}

// お気に入りリストのメモを更新
function* updateFavoriteMemo({
  payload,
}: {
  payload: {
    userId: string,
    favoriteListId: number | string,
    favorableId: number,
    memo: string,
    favoriteIndex: number,
    favoriteCardStates: Array<any>,
  },
}) {
  const {
    userId,
    favoriteListId,
    favorableId,
    memo,
    favoriteIndex,
    favoriteCardStates,
  } = payload
  const path = `1/users/${userId}/favorite_lists/${favoriteListId}/favorites`

  try {
    const updatedFavorite = yield call(RestClient.patchJson, path, {
      memo,
      favorable_type: 'Room',
      favorable_id: favorableId,
    })

    yield put(
      actions.successFavoriteMemo({ favoriteIndex, favorite: updatedFavorite }),
    )
    // 該当のテキストフィールドを閉じる
    yield put({
      type: SET_UI_STATE,
      payload: {
        favoriteCardStates: favoriteCardStates.map((state, index) => ({
          ...state,
          isOpenWritableMemo:
            index === favoriteIndex ? false : state.isOpenWritableMemo,
        })),
      },
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_FAVORITE_MEMO,
      payload: e.message,
    })
  }
}

/**
 ************************************************************
 * saga
 ************************************************************
 **/

export const favoriteSagas = [
  takeEvery(actionTypes.ADD_FAVORITE, addFavorite),
  takeEvery(actionTypes.REMOVE_FAVORITE, removeFavorite),
  takeEvery(actionTypes.FETCH_FAVORITE_LIST, fetchFavoriteList),
  takeEvery(actionTypes.FETCH_FAVORITE_LIST_DETAIL, fetchFavoriteListDetail),
  takeEvery(
    actionTypes.INITIALIZE_FAVORITE_LIST_DETAIL,
    initializeFavoriteListDetail,
  ),
  takeEvery(actionTypes.CREATE_FAVORITE_LIST, createFavoriteList),
  takeEvery(actionTypes.UPDATE_FAVORITE_LIST, updateFavoriteList),
  takeEvery(actionTypes.DELETE_FAVORITE_LIST, deleteFavoriteList),
  takeEvery(actionTypes.UPDATE_FAVORITE_MEMO, updateFavoriteMemo),
]



// WEBPACK FOOTER //
// ./src/redux/modules/favorite.js